import React, { createContext, useContext, useState, ReactNode } from 'react'
import { useHistory } from 'react-router-dom'
import { ROUTES } from '@sportninja/common/constants/app'
import {
  trackCompetitionStart,
  trackCompetitionSkip,
  trackCompetitionClose,
} from 'src/utils/analytics'

export type StructureType = 'single' | 'multiple' | 'conference' | null
export type RegistrationType = 'registration' | 'manual' | null

export interface Division {
  id: string
  name: string
  order: number
}

export interface Conference {
  id: string
  name: string
  order: number
  divisions: Division[]
}

export interface CompetitionSubmissionData {
  type: 'single' | 'multiple' | 'conferences'
  divisions?: Division[]
  conferences?: Conference[]
  registration_type: 'registration' | 'manual'
  schedule_id: string
}

interface CompetitionSetupState {
  currentStep: number
  structureType: StructureType
  divisionsData: Division[]
  conferencesData: Conference[]
  registrationType: RegistrationType
  singleDivisionName: string
}

interface CompetitionSetupContextType {
  state: CompetitionSetupState
  setStructureType: (type: StructureType) => void
  setDivisionsData: (data: Division[]) => void
  setConferencesData: (data: Conference[]) => void
  setRegistrationType: (type: RegistrationType) => void
  setSingleDivisionName: (name: string) => void
  buildSubmissionData: (
    overrideRegistrationType?: RegistrationType
  ) => CompetitionSubmissionData | null
  nextStep: () => void
  previousStep: (steps?: number) => void
  skipWizard: () => void
  closeWizard: () => void
  completeWizard: (redirectPath?: string) => void
  competitionId: string
  competitionName: string
  onRefetch?: () => void
}

const CompetitionSetupContext = createContext<
  CompetitionSetupContextType | undefined
>(undefined)

interface CompetitionSetupProviderProps {
  children: ReactNode
  onClose: () => void
  onSkip?: () => void
  onComplete?: () => void
  onRefetch?: () => void
  competitionId: string
  competitionName: string
}

export const CompetitionSetupProvider: React.FC<
  CompetitionSetupProviderProps
> = ({
  children,
  onClose,
  onSkip,
  onComplete,
  onRefetch,
  competitionId,
  competitionName,
}) => {
  const history = useHistory()

  const [state, setState] = useState<CompetitionSetupState>({
    currentStep: 1, // Start at step 1 (Structure)
    structureType: null,
    divisionsData: [],
    conferencesData: [],
    registrationType: null,
    singleDivisionName: '',
  })

  // Helper function to get step name for analytics
  const getStepName = (
    stepNumber: number,
    currentState?: CompetitionSetupState
  ): string => {
    const currentStructureType =
      currentState?.structureType || state.structureType
    switch (stepNumber) {
      case 1:
        return 'structure'
      case 2:
        return currentStructureType === 'conference' ? 'conference' : 'division'
      case 3:
        return 'finish'
      default:
        return 'unknown'
    }
  }

  // Track wizard start when component initializes
  React.useEffect(() => {
    trackCompetitionStart()
  }, [])

  const setStructureType = (type: StructureType) => {
    setState((prev) => ({ ...prev, structureType: type }))
  }

  const setDivisionsData = (data: Division[]) => {
    setState((prev) => ({ ...prev, divisionsData: data }))
  }

  const setConferencesData = (data: Conference[]) => {
    setState((prev) => ({ ...prev, conferencesData: data }))
  }

  const setRegistrationType = (type: RegistrationType) => {
    setState((prev) => ({ ...prev, registrationType: type }))
  }

  const setSingleDivisionName = (name: string) => {
    setState((prev) => ({ ...prev, singleDivisionName: name }))
  }

  const buildSubmissionData = (
    overrideRegistrationType?: RegistrationType
  ): CompetitionSubmissionData | null => {
    const registrationType = overrideRegistrationType || state.registrationType

    if (!state.structureType || !registrationType) {
      console.warn(
        '⚠️ CompetitionSetupContext: Missing required data for submission'
      )
      return null
    }

    const submissionData: CompetitionSubmissionData = {
      type:
        state.structureType === 'conference'
          ? 'conferences'
          : state.structureType,
      registration_type: registrationType,
      schedule_id: competitionId,
    }

    // Handle different structure types
    if (state.structureType === 'single') {
      // For single division, use the custom division name or fallback
      submissionData.divisions = [
        {
          id: 'single_division',
          name:
            state.singleDivisionName || competitionName || 'Single Division',
          order: 0,
        },
      ]
    } else if (state.structureType === 'multiple') {
      submissionData.divisions = state.divisionsData
    } else if (state.structureType === 'conference') {
      submissionData.conferences = state.conferencesData
    }

    return submissionData
  }

  const nextStep = () => {
    setState((prev) => {
      // Normal progression for all structure types
      return {
        ...prev,
        currentStep: Math.min(prev.currentStep + 1, 3),
      }
    })
  }

  const previousStep = (steps: number = 1) => {
    setState((prev) => ({
      ...prev,
      currentStep: Math.max(prev.currentStep - steps, 1),
    }))
  }

  const skipWizard = async () => {
    try {
      // Track wizard skip
      const currentStepName = getStepName(state.currentStep)
      trackCompetitionSkip(currentStepName)

      if (onSkip) {
        await onSkip()
      }
      onClose()
    } catch (error) {
      console.error('🚨 CompetitionSetupContext: Error skipping wizard:', error)
      onClose()
    }
  }

  const closeWizard = () => {
    // Track wizard close
    const currentStepName = getStepName(state.currentStep)
    trackCompetitionClose(currentStepName)

    onClose()
  }

  const completeWizard = async (redirectPath?: string) => {
    try {
      // Track wizard completion - this will be called from RegistrationStep with the choice
      // For now, we'll track with a generic choice since the specific choice is tracked in RegistrationStep

      if (onComplete) {
        await onComplete()
      }

      // If a redirect path is provided, navigate there
      if (redirectPath) {
        // Build the full path with competition ID
        const fullPath = `${ROUTES.SCHEDULE_ROOT}/${competitionId}${redirectPath}`
        history.push(fullPath)
      }

      onClose()
    } catch (error) {
      console.error(
        '🚨 CompetitionSetupContext: Error completing wizard:',
        error
      )
      onClose()
    }
  }

  const value: CompetitionSetupContextType = {
    state,
    setStructureType,
    setDivisionsData,
    setConferencesData,
    setRegistrationType,
    setSingleDivisionName,
    buildSubmissionData,
    nextStep,
    previousStep,
    skipWizard,
    closeWizard,
    completeWizard,
    competitionId,
    competitionName,
    onRefetch,
  }

  return (
    <CompetitionSetupContext.Provider value={value}>
      {children}
    </CompetitionSetupContext.Provider>
  )
}

export const useCompetitionSetup = () => {
  const context = useContext(CompetitionSetupContext)
  if (!context) {
    throw new Error(
      'useCompetitionSetup must be used within CompetitionSetupProvider'
    )
  }
  return context
}
